import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, doc, setDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { getAnalytics, isSupported } from 'firebase/analytics';

// Reverted to default Firebase auth domain (popup flow; no same-origin /__/auth proxy)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
};

const missingKeys = Object.entries(firebaseConfig)
  .filter(([, value]) => !value || value === 'undefined')
  .map(([key]) => key);

if (missingKeys.length > 0) {
  // eslint-disable-next-line no-console
  console.warn(
    `Firebase config is missing values for: ${missingKeys.join(
      ', ',
    )}. Double-check your environment variables.`,
  );
}

const firebaseApp = initializeApp(firebaseConfig);

export const auth = getAuth(firebaseApp);
if (typeof window !== 'undefined') {
  setPersistence(auth, browserLocalPersistence).catch(() => {});
}
export const db = getFirestore(firebaseApp);

// 🔥 Expose db and auth to window for manual testing (dev mode and staging only)
// For automated security tests, see: __tests__/services/firestoreSecurity.test.js
// Allow in development OR staging environment (not production)
// Expose DB/auth and security test only in local development (not staging/production)
const isDevelopment = process.env.NODE_ENV === 'development';

if (typeof window !== 'undefined' && isDevelopment) {
  window.__PONPON_DB__ = db;
  window.__PONPON_AUTH__ = auth;

  window.__TEST_SECURITY_ATTACKS__ = async () => {
    const { doc, updateDoc, setDoc } = await import('firebase/firestore');
    const userId = window.__PONPON_AUTH__.currentUser?.uid;

    if (!userId) {
      console.error('No user logged in. Please login first.');
      return;
    }

    const results = {};
    const userRef = doc(window.__PONPON_DB__, 'users', userId);

    try {
      await updateDoc(userRef, { tokens: 999999 });
      results.tokens = { blocked: false, message: 'VULNERABLE!' };
    } catch (error) {
      results.tokens = { blocked: true, code: error.code, message: 'PROTECTED' };
    }

    try {
      await updateDoc(userRef, { locker: [{ id: 'hacked-item' }] });
      results.locker = { blocked: false, message: 'VULNERABLE!' };
    } catch (error) {
      results.locker = { blocked: true, code: error.code, message: 'PROTECTED' };
    }

    try {
      await updateDoc(userRef, { maxLockerSlots: 999 });
      results.maxSlots = { blocked: false, message: 'VULNERABLE!' };
    } catch (error) {
      results.maxSlots = { blocked: true, code: error.code, message: 'PROTECTED' };
    }

    try {
      const batchRef = doc(window.__PONPON_DB__, 'users', userId, 'shippingBatches', 'fake');
      await setDoc(batchRef, { id: 'fake' });
      results.shippingBatches = { blocked: false, message: 'VULNERABLE!' };
    } catch (error) {
      results.shippingBatches = { blocked: true, code: error.code, message: 'PROTECTED' };
    }

    console.table(results);
    return results;
  };
}

// Initialize Analytics only in browser environment
let analytics = null;
if (typeof window !== 'undefined') {
  // Check if Analytics is supported before initializing
  isSupported()
    .then((supported) => {
      if (supported) {
        analytics = getAnalytics(firebaseApp);
      }
    })
    .catch((error) => {
      console.warn('Firebase Analytics initialization failed:', error);
    });
}

export { analytics };
export default firebaseApp;

// CHANGED: Add/update whitelist function - uses email as document ID
export const addToWhitelist = async ({ email, nickname, favoriteAnime, spinsUsed = 0, spinResult = null } = {}) => {
  if (!email || !nickname) {
    throw new Error('Email and nickname are required');
  }

  // Validate Firebase configuration
  if (!db) {
    const error = new Error('Firestore database is not initialized');
    console.error('Firestore DB check failed:', error);
    console.error('Firebase config:', {
      projectId: firebaseConfig.projectId,
      apiKey: firebaseConfig.apiKey ? '***' : 'MISSING',
      authDomain: firebaseConfig.authDomain,
    });
    throw error;
  }

  try {
    // Use email as document ID (normalized to lowercase)
    const emailId = email.trim().toLowerCase();
    const docRef = doc(db, 'whitelist', emailId);
    
    const updateData = {
      email: email.trim(),
      nickname: nickname.trim(),
      favoriteAnime: favoriteAnime ? favoriteAnime.trim() : null,
      source: 'Whitelist',
      spinsUsed: typeof spinsUsed === 'number' ? spinsUsed : 0,
      updatedAt: serverTimestamp(),
    };

    // If this is the first time (spinsUsed === 0), set createdAt
    // Otherwise, just update existing record
    if (spinsUsed === 0) {
      updateData.createdAt = serverTimestamp();
    }

    // Add spin result to array if provided
    if (spinResult) {
      updateData.spinResults = arrayUnion(spinResult);
    }

    console.log('Attempting to write to Firestore:', {
      collection: 'whitelist',
      documentId: emailId,
      projectId: firebaseConfig.projectId,
    });

    await setDoc(docRef, updateData, { merge: true });
    
    console.log('Successfully wrote to Firestore:', emailId);
    return { id: emailId, success: true };
  } catch (error) {
    console.error('Failed to add to whitelist:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      name: error.name,
      stack: error.stack,
    });
    
    // Provide more helpful error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please check Firestore security rules.');
    } else if (error.code === 'unavailable') {
      throw new Error('Firestore service is unavailable. Please check your internet connection.');
    } else if (error.code === 'failed-precondition') {
      throw new Error('Firestore operation failed. Please try again.');
    }
    
    throw error;
  }
};
